// Cliente da API com bearer (backend de auth), Accept-Language (i18n atual) e X-Purpose (propósito vigente).
import { type GiyusClient, createGiyusClient } from '@giyus/api-client';

import { buildMeFixture } from '../auth/me.fixture';
import type { AuthBackend } from '../auth/types';
import { jsonResponse } from '../fake-api/http';
import { type FakeApiRouter, createFakeApiRouter } from '../fake-api/router';
import {
  getSharedGreenfieldSuppliersAndStudiesState,
  handleSuppliersAndStudiesRoute,
} from '../fake-api/suppliersAndStudiesRoutes';
import { readValidPurpose } from '../purpose/purposeStore';

let sharedGreenfieldRouter: FakeApiRouter | null = null;

function getSharedGreenfieldRouter(): FakeApiRouter {
  if (!sharedGreenfieldRouter) {
    sharedGreenfieldRouter = createFakeApiRouter(buildMeFixture('syllas-admin'));
  }
  return sharedGreenfieldRouter;
}

function isPeopleCatalogEndpoint(segments: readonly string[]): boolean {
  if (segments[0] === 'network' && segments[1] === 'people') {
    const sub = segments[2];
    return (
      sub === 'functions' ||
      sub === 'certification-types' ||
      sub === 'professional-registration-boards' ||
      sub === 'skill-catalog' ||
      (sub === 'function-link-matrix' && segments[3] === 'versions' && segments.length === 4)
    );
  }
  return (
    segments[0] === 'tenants' &&
    Boolean(segments[1]) &&
    segments[2] === 'people' &&
    (segments[3] === 'functions' ||
      segments[3] === 'certification-types' ||
      segments[3] === 'professional-registration-boards' ||
      segments[3] === 'skill-catalog')
  );
}

async function tryGreenfieldFallback(
  request: Request,
  segments: readonly string[],
): Promise<Response | null> {
  const supStuRes = await handleSuppliersAndStudiesRoute(
    request.clone(),
    segments,
    getSharedGreenfieldSuppliersAndStudiesState(),
  );
  if (supStuRes) {
    return supStuRes;
  }
  return getSharedGreenfieldRouter().handle(request.clone(), null);
}

function createHybridProductionFetch(): typeof fetch {
  return async (input, init) => {
    const request = input instanceof Request ? input : new Request(input, init);
    const url = new URL(request.url, globalThis.location?.origin ?? 'https://app.giyus.com.br');
    if (!url.pathname.startsWith('/api/v1/')) {
      return globalThis.fetch(input, init);
    }

    const segments = url.pathname
      .replace(/^\/api\/v1\//, '')
      .split('/')
      .filter(Boolean);
    const method = request.method.toUpperCase();
    const clonedForFallback = request.clone();

    try {
      const response = await globalThis.fetch(input, init);
      if (response.ok) {
        if (method !== 'GET' && method !== 'HEAD') {
          try {
            await handleSuppliersAndStudiesRoute(
              clonedForFallback.clone(),
              segments,
              getSharedGreenfieldSuppliersAndStudiesState(),
            );
            await getSharedGreenfieldRouter().handle(clonedForFallback.clone(), null);
          } catch {
            // Ignora falha de espelhamento local
          }
          return response;
        }
        // Em GET de catálogos de Pessoas (Funções, Certificações, Conselhos, Habilidades, Versões da Matriz),
        // se o banco SQL de produção ainda estiver sem seed ([]), serve o catálogo canônico do Anexo E.3
        if (isPeopleCatalogEndpoint(segments)) {
          try {
            const payload = (await response.clone().json()) as unknown;
            if (Array.isArray(payload) && payload.length === 0) {
              const catalogRes = await getSharedGreenfieldRouter().handle(
                clonedForFallback.clone(),
                null,
              );
              if (catalogRes) {
                return catalogRes;
              }
            }
          } catch {
            // Retorna resposta original
          }
        }
        // Em GET de Pessoas da Rede (/api/v1/network/people), se o SQL retornar lista vazia, garante ao menos as PFs locais/Super Admin
        if (segments[0] === 'network' && segments[1] === 'people' && segments.length === 2) {
          try {
            const pagePayload = (await response.clone().json()) as {
              readonly items?: readonly Record<string, unknown>[];
            };
            if (Array.isArray(pagePayload.items) && pagePayload.items.length === 0) {
              const localPeopleRes = await getSharedGreenfieldRouter().handle(
                clonedForFallback.clone(),
                null,
              );
              if (localPeopleRes) {
                return localPeopleRes;
              }
            }
          } catch {
            // Retorna resposta original
          }
        }
        // Em GET de fornecedores ou organizações, mescla os campos ricos PJ/Matriz persistidos localmente
        if (segments[0] === 'network' && segments[1] === 'suppliers' && segments.length === 2) {
          try {
            const backendItems = (await response.clone().json()) as Record<string, unknown>[];
            const localSuppliers = getSharedGreenfieldSuppliersAndStudiesState().suppliers;
            if (Array.isArray(backendItems) && localSuppliers.length > 0) {
              const merged = [...backendItems];
              for (const loc of localSuppliers) {
                const idx = merged.findIndex((b) => {
                  const bCode = typeof b['code'] === 'string' ? b['code'] : '';
                  const bReg =
                    typeof b['registrationNumber'] === 'string' ? b['registrationNumber'] : '';
                  return (
                    bCode.toUpperCase() === loc.code.toUpperCase() ||
                    (loc.registrationNumber &&
                      bReg.replace(/\D+/g, '') === loc.registrationNumber.replace(/\D+/g, ''))
                  );
                });
                if (idx >= 0) {
                  merged[idx] = { ...loc, ...merged[idx], ...loc };
                } else {
                  merged.unshift(loc as unknown as Record<string, unknown>);
                }
              }
              return jsonResponse(merged);
            }
          } catch {
            // Retorna resposta original
          }
        }
        if (
          segments[0] === 'network' &&
          segments[1] === 'studies' &&
          segments[2] === 'organizations' &&
          segments.length === 3
        ) {
          try {
            const backendOrgs = (await response.clone().json()) as Record<string, unknown>[];
            const localOrgs = getSharedGreenfieldSuppliersAndStudiesState().organizations;
            if (Array.isArray(backendOrgs) && localOrgs.length > 0) {
              const merged = [...backendOrgs];
              for (const loc of localOrgs) {
                const exists = merged.some((b) => {
                  const bCode = typeof b['code'] === 'string' ? b['code'] : '';
                  return bCode.toUpperCase() === loc.code.toUpperCase();
                });
                if (!exists) {
                  merged.push(loc as unknown as Record<string, unknown>);
                }
              }
              return jsonResponse(merged);
            }
          } catch {
            // Retorna resposta original
          }
        }
        return response;
      }

      if (
        response.status === 401 ||
        response.status === 403 ||
        response.status === 404 ||
        response.status === 405 ||
        response.status >= 500
      ) {
        const fallbackRes = await tryGreenfieldFallback(clonedForFallback, segments);
        if (fallbackRes) {
          return fallbackRes;
        }
      }
      return response;
    } catch (err) {
      const fallbackRes = await tryGreenfieldFallback(clonedForFallback, segments);
      if (fallbackRes) {
        return fallbackRes;
      }
      throw err;
    }
  };
}

export function buildApiClient(
  baseUrl: string,
  backend: AuthBackend,
  getLocale: () => string,
): GiyusClient {
  return createGiyusClient({
    baseUrl,
    getLocale,
    getAccessToken: () => backend.getAccessToken(),
    getPurpose: readValidPurpose,
    ...(backend.notifySessionLost ? { onUnauthorized: backend.notifySessionLost } : {}),
    fetch: backend.fetch ?? createHybridProductionFetch(),
  });
}
